import { useState, useCallback } from 'react';
import { InternshipResult, ResultDocument } from '../types/internship';
import { useCachedData } from './useCachedData';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../utils/supabase';
import { createLogger } from '../utils/logger';

const logger = createLogger({ module: 'useInternshipResults' });

export const useInternshipResults = (focusId?: string) => {
  const { user } = useAuth();
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);
  
  // Fetch existing result
  const { 
    data: result, 
    loading, 
    error, 
    refresh 
  } = useCachedData<InternshipResult | null>(
    async () => {
      if (!focusId || !user) return null;
      
      try {
        // Query without .single() to handle empty results gracefully
        const { data: results, error } = await supabase
          .from('internship_results')
          .select('*')
          .eq('focus_id', focusId);
        
        if (error) {
          logger.error('Query error:', { 
            error, 
            code: error.code, 
            message: error.message,
            details: error.details,
            hint: error.hint,
            focusId
          });
          
          // Return null on any error
          return null;
        }
        
        // Check if we got any results
        if (!results || results.length === 0) {
          // No result found - this is expected for new internships
          logger.debug('No internship results found for focus:', { 
            focusId, 
            resultsLength: results?.length || 0 
          });
          setHasChecked(true);
          return null;
        }
        
        // Use the first result (there should only be one per focus)
        const data = results[0];
        
        // Fetch related documents separately
        let documents: ResultDocument[] = [];
        if (data && data.id) {
          const { data: docsData } = await supabase
            .from('result_documents')
            .select('id, file_id, document_type, description, uploaded_at')
            .eq('result_id', data.id);
            
          if (docsData) {
            documents = docsData.map((doc: any) => ({
              id: doc.id,
              fileId: doc.file_id,
              documentName: 'Document', // We'll need to fetch file name separately if needed
              documentType: doc.document_type,
              uploadedAt: doc.uploaded_at,
              description: doc.description
            }));
          }
        }
        
        // Transform the data to match our interface
        const transformed: InternshipResult = {
          id: data.id,
          focusId: data.focus_id,
          userId: data.user_id,
          outcomeType: data.outcome_type,
          outcomeDate: data.outcome_date,
          outcomeRecordedAt: data.outcome_recorded_at,
          offerDetails: data.offer_details,
          decisionStatus: data.decision_status,
          decisionDate: data.decision_date,
          decisionDeadline: data.decision_deadline,
          decisionFactors: data.decision_factors,
          feedbackReceived: data.feedback_received,
          rejectionStage: data.rejection_stage,
          rejectionReason: data.rejection_reason,
          lessonsLearned: data.lessons_learned,
          achievementsSummary: data.achievements_summary,
          wouldRecommend: data.would_recommend,
          isFinal: data.is_final,
          createdAt: data.created_at,
          updatedAt: data.updated_at,
          documents
        };
        
        return transformed;
      } catch (err) {
        logger.error('Error in useInternshipResults:', err);
        // For any unexpected errors, return null to prevent retries
        return null;
      }
    },
    {
      cacheKey: focusId ? `internship_result_${focusId}` : undefined,
      enabled: !!focusId && !!user,
      maxAge: 30 * 60 * 1000, // 30 minutes - longer cache time
      staleTime: 5 * 60 * 1000, // 5 minutes
      initialData: null, // Set initial data to prevent loading state on mount
    }
  );
  
  // Create new result
  const createResult = useCallback(async (data: Partial<InternshipResult>) => {
    if (!user || !focusId) {
      throw new Error('User or focus ID not available');
    }
    
    setCreating(true);
    try {
      const { data: newResult, error } = await supabase
        .from('internship_results')
        .insert({
          focus_id: focusId,
          user_id: user.id,
          outcome_type: data.outcomeType,
          outcome_date: data.outcomeDate || new Date().toISOString().split('T')[0],
          offer_details: data.offerDetails,
          decision_status: data.decisionStatus,
          decision_date: data.decisionDate,
          decision_deadline: data.decisionDeadline,
          decision_factors: data.decisionFactors,
          feedback_received: data.feedbackReceived,
          rejection_stage: data.rejectionStage,
          rejection_reason: data.rejectionReason,
          lessons_learned: data.lessonsLearned,
          achievements_summary: data.achievementsSummary,
          would_recommend: data.wouldRecommend,
          is_final: data.isFinal || false
        })
        .select()
        .single();
        
      if (error) throw error;
      
      // Clear cache and refresh
      await refresh();
      
      return newResult;
    } catch (error) {
      logger.error('Error creating internship result:', error);
      throw error;
    } finally {
      setCreating(false);
    }
  }, [user, focusId, refresh]);
  
  // Update existing result
  const updateResult = useCallback(async (updates: Partial<InternshipResult>) => {
    // Allow updates with an ID in the updates object
    const resultId = updates.id || result?.id;
    if (!user || !resultId) {
      throw new Error('No result to update');
    }
    
    setUpdating(true);
    try {
      // Build update object with only defined values
      const updateData: any = {};
      
      // Handle fields that can be updated
      if (updates.outcomeType !== undefined) updateData.outcome_type = updates.outcomeType;
      if (updates.outcomeDate !== undefined) updateData.outcome_date = updates.outcomeDate;
      if (updates.decisionStatus !== undefined) updateData.decision_status = updates.decisionStatus;
      if (updates.decisionDate !== undefined) updateData.decision_date = updates.decisionDate;
      if (updates.decisionDeadline !== undefined) updateData.decision_deadline = updates.decisionDeadline;
      if (updates.decisionFactors !== undefined) updateData.decision_factors = updates.decisionFactors;
      if (updates.feedbackReceived !== undefined) updateData.feedback_received = updates.feedbackReceived;
      if (updates.rejectionStage !== undefined) updateData.rejection_stage = updates.rejectionStage;
      if (updates.rejectionReason !== undefined) updateData.rejection_reason = updates.rejectionReason;
      if (updates.lessonsLearned !== undefined) updateData.lessons_learned = updates.lessonsLearned;
      if (updates.achievementsSummary !== undefined) updateData.achievements_summary = updates.achievementsSummary;
      if (updates.wouldRecommend !== undefined) updateData.would_recommend = updates.wouldRecommend;
      if (updates.isFinal !== undefined) updateData.is_final = updates.isFinal;
      
      // Special handling for offer_details based on outcome_type
      if (updates.outcomeType !== undefined) {
        if (updates.outcomeType === 'offer_received') {
          // For offer_received, ensure offer_details is at least an empty object
          updateData.offer_details = updates.offerDetails || {};
        } else {
          // For other outcome types, explicitly set to null
          updateData.offer_details = null;
        }
      } else if (updates.offerDetails !== undefined) {
        // If outcome_type isn't changing but offer_details is provided, update it
        updateData.offer_details = updates.offerDetails;
      }
      
      const { error } = await supabase
        .from('internship_results')
        .update(updateData)
        .eq('id', resultId);
        
      if (error) throw error;
      
      // Clear cache and refresh
      await refresh();
    } catch (error) {
      logger.error('Error updating internship result:', error);
      throw error;
    } finally {
      setUpdating(false);
    }
  }, [user, refresh]);
  
  // Link document to result
  const linkDocument = useCallback(async (fileId: string, documentType: string) => {
    if (!user || !result?.id) {
      throw new Error('No result to link document to');
    }
    
    try {
      const { error } = await supabase
        .from('result_documents')
        .insert({
          result_id: result.id,
          user_id: user.id,
          file_id: fileId,
          document_type: documentType
        });
        
      if (error) throw error;
      
      await refresh();
    } catch (error) {
      logger.error('Error linking document:', error);
      throw error;
    }
  }, [user, result, refresh]);
  
  // Delete existing result
  const deleteResult = useCallback(async (resultId: string, deleteFiles: boolean = false) => {
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    try {
      // If deleteFiles is true, first get the list of files to delete
      let filesToDelete: string[] = [];
      if (deleteFiles && result?.documents) {
        filesToDelete = result.documents.map(doc => doc.fileId);
      }
      
      // Delete the internship result
      const { error: deleteError } = await supabase
        .from('internship_results')
        .delete()
        .eq('id', resultId)
        .eq('user_id', user.id); // Extra safety check
        
      if (deleteError) throw deleteError;
      
      // If requested, delete associated files
      if (deleteFiles && filesToDelete.length > 0) {
        const { error: filesError } = await supabase
          .from('files')
          .update({ deleted_at: new Date().toISOString() }) // Soft delete
          .in('id', filesToDelete)
          .eq('user_id', user.id); // Extra safety check
          
        if (filesError) {
          logger.error('Error deleting files:', filesError);
          // Don't throw here - the result is already deleted
        }
      }
      
      // Clear cache and refresh
      await refresh();
      
      return true;
    } catch (error) {
      logger.error('Error deleting internship result:', error);
      throw error;
    }
  }, [user, result, refresh]);
  
  return {
    result,
    loading,
    error,
    creating,
    updating,
    refresh,
    createResult,
    updateResult,
    linkDocument,
    deleteResult
  };
};